import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  EventEmitter,
  OnDestroy,
  Output,
  Signal,
  signal,
  input
} from "@angular/core";
import {
  ActionSheetController,
  AlertButton,
  AlertController,
  AlertInput,
  IonicModule,
  LoadingController,
  ModalController,
  NavController,
  ToastController,
} from "@ionic/angular";
import { Store } from "@ngrx/store";
import {
  selectHasLoadedAttachment,
  selectSelectedStudent,
} from "../../ngrx/students.selectors";
import {
  CommonModule,
  DatePipe,
  DecimalPipe,
  NgOptimizedImage,
} from "@angular/common";
import { StudentDto } from "../../interfaces/student_dto";
import { ColorService } from "../../../shared/services/color.service";
import { FormsModule } from "@angular/forms";
import {
  deleteStudent,
  updateStudent,
  downloadAttachment,
  deleteAttachment,
  loadStudent,
} from "../../ngrx/students.actions";
import { ActivatedRoute } from "@angular/router";
import { isPlatform } from "@ionic/angular";
import { DialogService } from "src/app/shared/services/dialog.service";
import { selectInvoice } from "src/app/office/invoice-ngrx/invoices.actions";
import { OrderPipe } from "src/app/shared/pipes/order.pipe";
import { AppointmentDto } from "src/app/calendar/interfaces/appointment_dto";
import { selectExamsByStudentId, selectLessonsByStudentId, selectPastLessonsByStudentId, selectUpcomingLessonsByStudentId } from "src/app/calendar/ngrx/calendar.selectors";
import { LessonAccordionComponent } from "../../components/lesson-accordion/lesson-accordion.component";
import { InvoiceDto } from "src/app/office/interfaces/invoice_dto";
import { PaymentDto } from "src/app/office/interfaces/payment_dto";
import { selectInvoicesByStudentId } from "src/app/office/invoice-ngrx/invoices.selectors";
import { InvoiceItemComponent } from "src/app/office/components/invoice-item/invoice-item.component";
import { ReminderService } from "src/app/supabase/services/reminder.service";
import { selectPaymentsByStudentId } from "src/app/office/payment-ngrx/payments.selectors";
import { PaymentItemComponent } from "src/app/office/components/payment-item/payment-item.component";
import { InvoiceItemDto } from "src/app/office/interfaces/invoice_item_dto";
import { NgCircleProgressModule } from "ng-circle-progress";
import {
  selectEdcuationCategoryByCategory,
  selectLastEducationBodyLessonRatingsForStudent,
  selectServices,
  selectSubscriptions,
} from "src/app/settings/ngrx/settings.selectors";
import { selectSetting } from "src/app/settings/ngrx/settings.action";
import { EducationCategoryDto } from "src/app/settings/interfaces/education_category_dto";
import { EducationTitleDto } from "src/app/settings/interfaces/education_title_dto";
import { selectCustomer } from "src/app/auth/ngrx/customer.selectors";
import { ReportGeneratorDto } from "src/app/report-generation/interfaces/report_generator_dto";
import { Capacitor } from "@capacitor/core";
import { FileService } from "src/app/supabase/services/files.service";
import { AuthService } from "src/app/supabase/services/auth.service";
import { Base64Service } from "src/app/shared/services/base64.service";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { FileItemComponent } from "src/app/report-generation/components/file-item/file-item.component";
import { LessonRatingDto } from "src/app/lessons/interfaces/lesson_rating";
import { Subject, takeUntil } from "rxjs";
import { calculateTotalTime } from "src/app/utils/DateUtils";
import * as moment from 'moment';
import { selectPayment } from "src/app/office/payment-ngrx/payments.actions";
import { ProfileImageModalComponent } from "../../components/profile-image-modal/profile-image-modal.component";

@Component({
    selector: "app-student-detail",
    templateUrl: "./student-detail.component.html",
    styleUrls: ["./student-detail.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        DatePipe,
        FormsModule,
        OrderPipe,
        LessonAccordionComponent,
        InvoiceItemComponent,
        PaymentItemComponent,
        DecimalPipe,
        NgCircleProgressModule,
        FileItemComponent,
        CommonModule,
        NgOptimizedImage
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class StudentDetailComponent implements OnDestroy {
  readonly mobile = input(true);
  @Output() unselect = new EventEmitter<void>();

  isLoaded$ = this.store.select(selectHasLoadedAttachment);
  studentSignal = this.store.selectSignal(selectSelectedStudent);
  subscriptionsSignal = this.store.selectSignal(selectSubscriptions);
  servicesSignal = this.store.selectSignal(selectServices);
  customerSignal = this.store.selectSignal(selectCustomer);
  section = "details";
  sectionList = ["details", "invoices", "lessons", "education"];
  originPage: string;
  presentingElement = null;
  invoicesSignal: Signal<InvoiceDto[]> = signal<InvoiceDto[]>(null);
  paymentsSignal: Signal<PaymentDto[]> = signal<PaymentDto[]>(null);
  oldAppointmentsSignal = computed(() => this.store.selectSignal(
    selectPastLessonsByStudentId(this.studentSignal()?.student_id)
  )());
  upcomingAppointmentsSignal = computed(() => this.store.selectSignal(
    selectUpcomingLessonsByStudentId(this.studentSignal()?.student_id)
  )());
  examsSignal = computed(() => this.store.selectSignal(
    selectExamsByStudentId(this.studentSignal()?.student_id)
  )());
  ratingsSignal: Signal<LessonRatingDto[]> = signal(null);
  educationCategorySignal: Signal<EducationCategoryDto> =
    signal<EducationCategoryDto>(null);
  isSharePluginAvailable: boolean;
  androidFakeLoading = signal<boolean>(isPlatform("android") ? true : false);
  loading = signal<boolean>(true);
  studentId: number;
  public today = moment();
  private destroyed$ = new Subject<void>();
  protected readonly JSON = JSON;
  calculateTotalTime = calculateTotalTime;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private actionSheetCtrl: ActionSheetController,
    private alertController: AlertController,
    private colorService: ColorService,
    private navController: NavController,
    private dialogService: DialogService,
    private reminderService: ReminderService,
    private toastCtrl: ToastController,
    private fileService: FileService,
    private authService: AuthService,
    private base64Service: Base64Service,
    private loadingCtrl: LoadingController,
    private modalCtrl: ModalController
  ) {
    effect(() => this.loadData(), { allowSignalWrites: true });
    this.isSharePluginAvailable =
      Capacitor.isPluginAvailable("Share") && Capacitor.isNativePlatform();
    this.originPage = isPlatform("ios")
      ? this.route.snapshot.paramMap.get("originPage")
      : "";
  }

  loadData() {
    if (!this.studentSignal()) this.unselect.emit();
    if (this.loading() || this.studentSignal().student_id === this.studentId)
      return;
    // Update signals
    this.store.dispatch(
      loadStudent({ studentId: this.studentSignal().student_id })
    );
    this.educationCategorySignal = this.store.selectSignal(
      selectEdcuationCategoryByCategory(this.studentSignal().category)
    );
    this.ratingsSignal = this.store.selectSignal(
      selectLastEducationBodyLessonRatingsForStudent(
        this.studentSignal().student_id
      )
    );
    this.invoicesSignal = this.store.selectSignal(
      selectInvoicesByStudentId(this.studentSignal().student_id)
    );
    this.paymentsSignal = this.store.selectSignal(
      selectPaymentsByStudentId(this.studentSignal().student_id)
    );
    this.studentId = this.studentSignal().student_id;
  }

  async ionViewDidEnter() {
    if (this.loading()) {
      this.loading.set(false);
    }

    const loading = await this.loadingCtrl.create({
      message: "Lade Datei...",
      duration: 5000,
      spinner: "circles",
    });

    await this.isLoaded$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((loaded) => {
        if (loaded === false) {
          loading.present();
        } else {
          loading.dismiss();
        }
      });
    this.androidFakeLoading.set(false);
    this.presentingElement = document.getElementById("main-content");
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ionViewDidLeave() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  openEducationCard() {
    this.dialogService.openEducationCard(
      this.educationCategorySignal(),
      this.studentSignal(),
      null,
      false
    );
  }

  getGoogleMapsLink(street: string, postal_code: string, city: string) {
    const link = isPlatform("ios") ? "maps://www.google.com/?q=" + street + "+" + postal_code + "+" + city : "https://www.google.com/maps/search/?api=1&query=" + street + "+" + postal_code + "+" + city;
    return link;
  }

  getProgress(educationTitle: EducationTitleDto) {
    const educationBodies = educationTitle.education_body;

    const lessonRatings = this.ratingsSignal()?.filter((rating) =>
      educationBodies.some(
        (body) => body.education_body_id === rating.education_body_id
      )
    );
    const totalRatings = educationTitle.education_body.length * 6;
    const totalSum = lessonRatings
      ?.map((rating) => rating.rating)
      .reduce((a, b) => a + b, 0);

    return (totalSum / totalRatings) * 100;
  }

  getNumberOfOpenInvoices(invoices: InvoiceDto[]): number {
    return invoices?.filter(
      (invoice) =>
        (invoice.paid_amount ?? 0) !== invoice.total_amount &&
        new Date(invoice.due_date) >= new Date()
    ).length;
  }

  getAmountOfOpenInvoices(invoices: InvoiceDto[]): number {
    return invoices
      ?.filter((invoice) => (invoice.paid_amount ?? 0) !== invoice.total_amount && new Date(invoice.due_date) >= new Date())
      .reduce(
        (total, invoice) =>
          total + (invoice.total_amount - invoice.paid_amount),
        0
      );
  }

  getNumberOfPaidInvoices(invoices: InvoiceDto[]): number {
    return invoices?.filter(
      (invoice) => (invoice.paid_amount ?? 0) === invoice.total_amount
    ).length;
  }

  getAmountOfPaidInvoices(invoices: InvoiceDto[]): number {
    return invoices
      ?.filter((invoice) => (invoice.paid_amount ?? 0) !== 0)
      .reduce((total, invoice) => total + invoice.paid_amount, 0);
  }

  getNumberOfOverdueInvoices(invoices: InvoiceDto[]): number {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    return invoices?.filter(
      (invoice) =>
        new Date(invoice.due_date) < currentDate &&
        (invoice.paid_amount ?? 0) !== invoice.total_amount
    ).length;
  }

  getAmountOfOverdueInvoices(invoices: InvoiceDto[]): number {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    return invoices
      ?.filter(
        (invoice) =>
          new Date(invoice.due_date) < currentDate &&
          (invoice.paid_amount ?? 0) !== invoice.total_amount
      )
      .reduce((total, invoice) => total + invoice.total_amount, 0);
  }

  calculateTotalAmount(invoices: InvoiceDto[]): number {
    return invoices?.reduce(
      (total, invoice) => total + invoice.total_amount,
      0
    );
  }

  calculatePaidAmount(payments: PaymentDto[]): number {
    return payments?.reduce((total, payment) => total + payment.amount, 0);
  }

  sendReminder(reminder: any) {
    this.reminderService.sendReminder(reminder);
  }

  getColor(student: StudentDto) {
    return this.colorService.getColor(student.forename);
  }

  convertToDate(dateString) {
    return new Date(dateString);
  }

  openInvoice(invoice) {
    this.store.dispatch(selectInvoice({ invoice: invoice }));
    this.dialogService.openInvoiceDialog(invoice);
  }

  openPayment(payment) {
    this.store.dispatch(selectPayment({ payment: payment }));
    this.dialogService.openPaymentDialog(payment);
  }

  deleteStudent() {
    this.store.dispatch(deleteStudent({ student: this.studentSignal() }));
    this.navController.pop();
  }

  openStudentModal() {
    this.dialogService.openNewStudentDialog(this.studentSignal());
  }

  async openNewInvoiceDialog(appointment?: AppointmentDto) {
    if (
      this.customerSignal().iban &&
      this.customerSignal().brand &&
      this.customerSignal().city &&
      this.customerSignal().mail_customer &&
      this.customerSignal().postal_code &&
      this.customerSignal().street
    ) {
      let newInvoice = {
        student_id: this.studentSignal(),
        invoice_items: [
          appointment
            ? ({
                id: appointment.Id,
                appointment_id: appointment.Id,
                prize: appointment.Prize,
                name: appointment.Type || appointment.Subject,
                date: appointment.StartTime.toISOString().slice(0, 10),
                amount: appointment.Lessons,
                unit: appointment.Lessons > 1 ? "Lektionen" : "Lektion",
                initial_prize: appointment.Prize,
                subscription_payment: appointment.SubscriptionPayment,
              } as InvoiceItemDto)
            : null,
        ],
      };
      this.dialogService.openNewInvoiceDialog(newInvoice);
    } else {
      this.store.dispatch(selectSetting({ setting: "company" }));
      await this.dialogService.openSettings(
        "company",
        "Um diese Funktion nutzen zu können, benötigen wir weitere Informationen. Möchtest du sie in den Einstellungen hinterlegen?"
      );
    }
  }

  async openNewPaymentDialog() {
    if (
      this.customerSignal().iban &&
      this.customerSignal().brand &&
      this.customerSignal().city &&
      this.customerSignal().mail_customer &&
      this.customerSignal().postal_code &&
      this.customerSignal().street
    ) {
      let newPayment = {
        student: this.studentSignal(),
      };
      this.dialogService.openNewPaymentDialog(newPayment);
    } else {
      this.store.dispatch(selectSetting({ setting: "company" }));
      await this.dialogService.openSettings(
        "company",
        "Um diese Funktion nutzen zu können, benötigen wir weitere Informationen. Möchtest du sie in den Einstellungen hinterlegen?"
      );
    }
  }

  async openNewSubscriptionAlert() {
    if (this.subscriptionsSignal().length === 0) {
      this.store.dispatch(selectSetting({ setting: "lessons" }));
      this.dialogService.openSettings(
        "lessons",
        "Um ein Abo zu verkaufen, musst du zuerst deine Angebote konfigurieren. Möchtest du sie in den Einstellungen hinterlegen?"
      );
      return;
    }

    if (
      this.customerSignal().iban &&
      this.customerSignal().brand &&
      this.customerSignal().city &&
      this.customerSignal().mail_customer &&
      this.customerSignal().postal_code &&
      this.customerSignal().street
    ) {
      this.dialogService.openNewSubscriptionAlert(
        this.studentSignal(),
        this.subscriptionsSignal()
      );
    } else {
      this.store.dispatch(selectSetting({ setting: "company" }));
      await this.dialogService.openSettings(
        "company",
        "Um diese Funktion nutzen zu können, benötigen wir weitere Informationen. Möchtest du sie in den Einstellungen hinterlegen?"
      );
    }
  }

  openNewLessonDialog() {
    if (this.servicesSignal().length === 0) {
      this.store.dispatch(selectSetting({ setting: "lessons" }));
      this.dialogService.openSettings(
        "lessons",
        "Um eine neue Fahrstunde anzulegen, musst du zuerst deine Lektionen konfigurieren. Möchtest du sie in den Einstellungen hinterlegen?"
      );
      return;
    }
    let newLesson = {
      student: this.studentSignal(),
      location: this.studentSignal().location,
    };

    this.dialogService.openNewLessonDialog(newLesson);
  }

  async presentDeleteActionSheet() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: "Aktionen",
      buttons: this.studentSignal().is_archived
        ? this.deleteActionArchivedSheetButtons
        : this.deleteActionActiveSheetButtons,
    });

    await actionSheet.present();
  }

  public deleteActionArchivedSheetButtons = [
    {
      text: "Reaktivieren",
      handler: () => {
        this.activateStudent();
      },
    },
    {
      text: "Löschen",
      role: "destructive",
      handler: () => {
        this.presentDeleteAlert();
      },
    },
    {
      text: "Abbrechen",
      role: "cancel",
      data: {
        action: "cancel",
      },
    },
  ];

  public deleteActionActiveSheetButtons = [
    {
      text: "Archivieren",
      handler: () => {
        this.archiveStudent();
      },
    },
    {
      text: "Löschen",
      role: "destructive",
      handler: () => {
        this.presentDeleteAlert();
      },
    },
    {
      text: "Abbrechen",
      role: "cancel",
      data: {
        action: "cancel",
      },
    },
  ];

  archiveStudent() {
    let studentArchiveRequest = { ...this.studentSignal(), is_archived: true };
    delete studentArchiveRequest.profile_picture_url;
    this.store.dispatch(updateStudent({ student: studentArchiveRequest }));
    this.navController.navigateBack("/home/tab1");
  }

  activateStudent() {
    let studentArchiveRequest = { ...this.studentSignal(), is_archived: false };
    delete studentArchiveRequest.profile_picture_url;
    this.store.dispatch(updateStudent({ student: studentArchiveRequest }));
    this.navController.navigateBack("/home/tab1");
  }

  async presentDeleteAlert() {
    const alert = await this.alertController.create({
      header: "Achtung!",
      subHeader: "Bist du sicher?",
      message:
        "Dadurch werden alle Daten des Schülers gelöscht (inkl. Rechnungen, Zahlungen, Termine, etc.)",
      buttons: this.deleteAlertButtons,
      inputs: this.deleteAlertInputs,
    });

    alert.present().then(() => {

      /** disable the confirm button initial */
      document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').setAttribute('disabled', 'true');
      (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)') as HTMLElement).style.opacity = '0.5';
      return;
  });
  }

  public deleteAlertInputs = [
    {
      type: "checkbox" as "checkbox",
      label: "Ich bestätige, dass ich die Daten unwiderruflich löschen möchte.",
      handler: () => {
        this.handleConfirmButtonState();
      }
    } as AlertInput
  ]

  public deleteAlertButtons = [
    {
      text: "Abbrechen",
      role: "cancel",
      handler: () => {
        this.alertController.dismiss();
      },
    },
    {
      text: "OK",
      role: "confirm",
      disabled: true,
      handler: () => {
        this.deleteStudent();
        this.alertController.dismiss();
      },
      cssClass: "my-button-disabled"
    } as AlertButton,
  ];

  handleConfirmButtonState() {
    if (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').hasAttribute('disabled')) {
      document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').removeAttribute('disabled');
      (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)') as HTMLElement).style.opacity = '1';
    }
    else {
      document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').setAttribute('disabled', 'true');
      (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)') as HTMLElement).style.opacity = '0.5';
    }
  }

  openExams() {
    this.dialogService.openExamListDialog(
      this.examsSignal(),
      this.studentSignal()
    );
  }

  async getPdfSrc(attachment: string) {
    let userId = await this.authService.getCurrentUserId();
    let pdf = await this.fileService.downloadFile("attachments", {
      path: `${userId}/${this.studentSignal().student_id}/${attachment}`,
      title: "",
    });

    if (pdf === null) {
      const toast = this.toastCtrl.create({
        message: "Die Datei kann nicht geladen werden",
        duration: 3000,
      });

      toast.then((toast) => toast.present());
      return;
    }
    const blob = new Blob([pdf]);
    return URL.createObjectURL(blob);
  }

  async shareAttachment(attachment: string) {
    let blobUrl = await this.getPdfSrc(attachment);
    var base64 = await this.base64Service.convertToBase64(blobUrl);
    return Filesystem.writeFile({
      path: attachment,
      data: base64,
      directory: Directory.Cache,
    })
      .then(() => {
        return Filesystem.getUri({
          directory: Directory.Cache,
          path: attachment,
        });
      })
      .then((uriResult) => {
        return Share.share({
          title: attachment,
          text: attachment,
          url: uriResult.uri,
        });
      });
  }

  async openAttachment(attachment: string) {
    let attachmentFileInfo = {
      path: `${this.customerSignal().user_id}/${
        this.studentSignal().student_id
      }/${attachment}`,
      title: "",
    };

    let fileName = attachment;

    this.store.dispatch(
      downloadAttachment({
        container: "attachments",
        fileName,
        attachmentFileInfo,
      })
    );
  }

  deleteAttachment(attachment: string) {
    this.store.dispatch(
      deleteAttachment({ student: this.studentSignal(), attachment })
    );
  }

  generateReport() {
    let reportGenerator = {
      report_id: 1,
      student_id: this.studentSignal().student_id,
    };

    let studentName = this.studentSignal().forename;

    this.dialogService.openReportDialog(
      reportGenerator as ReportGeneratorDto,
      studentName,
      "student_detail_screen"
    );
  }

  getAge(birthday: string): number {
    if (!birthday) return 0;
    return moment().diff(moment(birthday), 'years');
  }

  // Check if student can be registered for examination
  canRegisterForExam(student: StudentDto): boolean {
    if (!student) return false;
    
    // Check if LFA is valid (end date is in the future)
    const isLfaValid = this.isLfaValid(student);
    if (!isLfaValid) return false;
    
    // Check if VKU is completed
    if (!student.vku_completed) return false;
    
    // Check age requirement
    return this.meetsAgeRequirement(student);
  }

  // Check if LFA is valid (end date is in the future)
  isLfaValid(student: StudentDto): boolean {
    if (!student.lfa_end) return false;
    return moment(student.lfa_end).isAfter(moment());
  }

  // Check if student meets age requirement for examination
  meetsAgeRequirement(student: StudentDto): boolean {
    if (!student.lfa_start || !student.birthday) return false;
    
    const lfaStartDate = moment(student.lfa_start);
    const birthDate = moment(student.birthday);
    
    // Calculate age at LFA start date
    const ageAtLfaStart = lfaStartDate.diff(birthDate, 'years');
    
    // If student was under 20 at LFA start, they need to wait 1 year
    if (ageAtLfaStart < 20) {
      const oneYearAfterLfaStart = moment(lfaStartDate).add(1, 'year');
      return moment().isSameOrAfter(oneYearAfterLfaStart);
    }
    
    // If student was 20 or older, no waiting period required
    return true;
  }

  // Calculate age between two dates
  calculateAge(birthDate: Date, currentDate: Date): number {
    return moment(currentDate).diff(moment(birthDate), 'years');
  }

  // Get days since LFA start
  getDaysSinceLfaStart(startDate: string): number {
    if (!startDate) return 0;
    return moment().startOf('day').diff(moment(startDate).startOf('day'), 'days');
  }

  // Get formatted date for display
  getFormattedDate(dateString: string): string {
    return dateString ? moment(dateString).format("DD.MM.YYYY") : "";
  }

  // Add this method to calculate when the age requirement will be met
  getAgeRequirementDate(student: StudentDto): string {
    if (!student.lfa_start || !student.birthday) return '';
    
    const lfaStartDate = moment(student.lfa_start);
    const birthDate = moment(student.birthday);
    
    // Calculate age at LFA start date
    const ageAtLfaStart = lfaStartDate.diff(birthDate, 'years');
    
    // If student was under 20 at LFA start, they need to wait 1 year
    if (ageAtLfaStart < 20) {
      const oneYearAfterLfaStart = moment(lfaStartDate).add(1, 'year');
      return oneYearAfterLfaStart.format('DD.MM.YYYY');
    }
    
    // If student was 20 or older, no waiting period required
    return '';
  }

  calculateOverallProgress(): number {
    if (!this.educationCategorySignal() || !this.educationCategorySignal().education_title) {
      return 0;
    }
    
    const titles = this.educationCategorySignal().education_title;
    if (titles.length === 0) return 0;
    
    let totalProgress = 0;
    titles.forEach(title => {
      totalProgress += this.getProgress(title);
    });
    
    return Math.round(totalProgress / titles.length);
  }

  async openProfileImage(imageUrl: string) {
    const modal = await this.modalCtrl.create({
      component: ProfileImageModalComponent,
      cssClass: 'profile-image-modal',
      showBackdrop: true,
      backdropDismiss: true,
      componentProps: {
        imageUrl: imageUrl
      }
    });

    await modal.present();
  }
}
